import {
    differenceInSeconds,
    differenceInMinutes,
    differenceInHours,
    differenceInDays,
    isBefore,
    intervalToDuration,
    formatDistanceToNow
} from 'date-fns';

// For formatting time until lot starts (during auction PREVIEW)
export const getTimeUntilString = (futureDateTime: Date): string => {
    const now = new Date();

    if (isBefore(futureDateTime, now)) {
        return "Auction started";
    }

    const seconds = differenceInSeconds(futureDateTime, now);
    const minutes = differenceInMinutes(futureDateTime, now);
    const hours = differenceInHours(futureDateTime, now);
    const days = differenceInDays(futureDateTime, now);

    if (seconds <= 60) {
        return "Starting soon";
    } else if (minutes < 60) {
        return `Starting in ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    } else if (hours < 24) {
        return `Starting in ${hours} ${hours === 1 ? "hour" : "hours"}`;
    } else {
        // Smart rounding: round up to next day if 12+ hours remain
        const roundedDays = (hours % 24) >= 12 ? days + 1 : days;
        return `Starting in ${roundedDays} ${roundedDays === 1 ? "day" : "days"}`;
    }
};

// For formatting time left in a lot (during live auction)
export const formatTimeLeft = (milliseconds: number | undefined) => {
    if (!milliseconds) return "";

    if (milliseconds <= 0) return "Closed";

    // Convert milliseconds directly to days, hours, minutes, seconds
    const totalSeconds = Math.floor(milliseconds / 1000);
    const validDays = Math.floor(totalSeconds / (24 * 60 * 60));
    const validHours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    const validMinutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const validSeconds = Math.floor(totalSeconds % 60);

    if (validDays > 1) {
        // Smart rounding: round up to next day if 12+ hours remain, otherwise round down
        const roundedDays = validHours >= 12 ? validDays + 1 : validDays;
        return `${roundedDays} days left`;
    } else if (validDays === 1) {
        return `${validDays} day left`;
    } else if (validHours >= 6) {
        return `${validHours}h left`;
    } else if (validHours > 0) {
        return `${validHours}h ${String(validMinutes).padStart(2, "0")}m left`;
    } else if (validMinutes > 0) {
        return `${String(validMinutes).padStart(2, "0")}:${String(validSeconds).padStart(2, "0")}`;
    } else if (validSeconds > 0) {
        return `00:${String(validSeconds).padStart(2, "0")}`;
    } else {
        return "Closed";
    }
};

export const formatDateDistance = (date: Date): string => {
    const now = new Date();
    const diffHours = differenceInHours(date, now);

    if (Math.abs(diffHours) < 24 || Math.abs(diffHours) > 24 * 30) {
        return formatDistanceToNow(date, { addSuffix: true });
    }

    const diffDays = differenceInDays(date, now);
    const remainderHours = Math.abs(diffHours) % 24;

    let roundedDays = Math.abs(diffDays);
    if (remainderHours >= 12) {
        roundedDays += 1;
    }
    
    const plural = roundedDays === 1 ? "" : "s";

    if (isBefore(now, date)) {
        return `in ${roundedDays} day${plural}`;
    }

    return `${roundedDays} day${plural} ago`;
};
